<template>
  <div class="staff">
    <v-overlay :value="loading" z-index="4">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <div v-if="isPracticeAdmin" class="programs_filter">
      <v-select
        v-model="programs"
        :items="availablePrograms"
        :item-text="'name'"
        return-object
        chips
        label="Filter by programs"
        clearable
        multiple
        @input="updateQueryParams"
        placeholder="Select program"
      >
        <template v-slot:selection="{ item, index }">
          <v-chip v-if="index === 0">
            <span>{{ item.name }}</span>
          </v-chip>
          <span v-if="index === 1" class="grey--text text-caption">
            (+{{ programs.length - 1 }} others)
          </span>
        </template>
      </v-select>
    </div>
    <main-user-table
      :headers="headers"
      :items="practiceUsers"
      :items-per-page="queryParams.pageSize"
      :server-items-length="pagination.totalCount || -1"
      :page="queryParams.page"
      :row-component="'practice-users-table-row'"
      @to-user="toUser"
      @action:get-data="getDataAction"
    />
    <v-btn
      v-if="isAnyAdminRole && floatingButton"
      :disabled="!isPracticeActive"
      color="primary"
      fixed
      bottom
      right
      fab
      large
      elevation="0"
      @click="addNewPracticeUser"
    >
      <v-icon size="24">mdi-plus</v-icon>
    </v-btn>
    <v-dialog
      v-model="confirmationPopupWelcomeMessage"
      persistent
      max-width="290"
      @click:outside="onClickOutsideDialog"
      @keydown="onKeydownDialog"
    >
      <v-card>
        <v-card-title class="headline">Welcome email</v-card-title>
        <v-card-text
          >Do you want to send a message to the user with all access
          information?</v-card-text
        >
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="rounded-pill"
            text
            @click="confirmationPopupWelcomeMessage = false"
            >Cancel</v-btn
          >
          <v-btn
            class="rounded-pill elevation-0"
            color="accent"
            @click="
              confirmationPopupWelcomeMessage = false
              prepareSendNewUserEmail()
            "
            >Ok</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <practice-user-dialog
      :show-dialog="showPracticeUserDialog"
      @close-dialog="closePracticeUserDialog"
      :practice-id="practiceId"
      :user="practiceUser"
      @on-submit="onUserSubmit"
      @openDeactivationDialog="showDeactivationDialog = true"
      @openNewUserEmailDialog="confirmationPopupWelcomeMessage = true"
      @openDeleteUserDialog="showConfirmationPopupDelete = true"
    />
    <remove-user-dialog
      :show-dialog="showConfirmationPopupDelete"
      :delete-title="
        'This action will remove this user from app. This action is irreversible! Proceed with caution.'
      "
      :delete-button-title="'Remove user'"
      @confirmDeleteUser="deletePracticeUser"
      @close-dialog="onCloseConfirmationDeleteDialog"
    />
    <new-practice-user-dialog
      :survey-available="surveyAvailable"
      :show-dialog="showNewPracticeUserDialog"
      @close-dialog="closeNewPracticeUserDialog"
      :practice-id="practiceId"
      @on-submit="onNewUserSubmit"
    />
    <deactivation-dialog
      :show-dialog="showDeactivationDialog"
      :user="practiceUser"
      :participants="practiceUserPatients"
      :participantsPagination="practiceUserPatientsPagination"
      :advisors="computedDoctors"
      :currentAdvisor="practiceUser"
      :reloader="deactivationDialogReloader"
      @onInput="changeAdvisor"
      @onSave="toggleActiveState"
      @close-dialog="closePracticeUserDeactivationDialog"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import NewPracticeUserDialog from '@/components/dialogs/NewPracticeUserDialog.vue'
import DeactivationDialog from '@/components/dialogs/PracticeUserDeactivationDialog.vue'
import PracticeUserDialog from '@/components/dialogs/PracticeUserDialog.vue'
import RemoveUserDialog from '@/components/dialogs/RemoveUserDialog.vue'
import RouterMixin from '@/mixins/router'
import MainUserTable from '@/components/tables/MainUserTable'
import { debounce } from '@/helpers/debounce'

export default {
  name: 'PracticeUsers',
  components: {
    MainUserTable,
    NewPracticeUserDialog,
    PracticeUserDialog,
    DeactivationDialog,
    RemoveUserDialog,
  },
  mixins: [RouterMixin],
  data() {
    return {
      floatingButton: true,
      headers: [
        { sortable: false },
        { text: 'Name', value: 'fullName' },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Programs', value: 'programs', sortable: false },
        { text: 'Role', value: 'roleId' },
        { text: 'Date added', value: 'creationDate' },
        { text: 'Last activity', value: 'lastActivity', sortable: false },
      ],
      surveyAvailable: 'Active',
      showNewPracticeUserDialog: false,
      showPracticeUserDialog: false,
      showDeactivationDialog: false,
      practiceDoctors: [],
      deactivationDialogReloader: 0,
      confirmationPopupWelcomeMessage: false,
      showConfirmationPopupDelete: false,
      defaultQueryParams: {
        sortBy: 'fullName',
        sortDirection: 'desc',
      },
      programs: [],
    }
  },
  watch: {
    async practiceUser(user) {
      if (this.isAdmin) {
        this.getBhUsers(this.bhUsersQuery)
      }

      if (user.practiceId) {
        const doctors = await this.getPracticeUsers({
          id: user.practiceId,
          params: this.doctorsQuery,
          returnData: true,
        })

        this.practiceDoctors = doctors || []
      }
    },
    $route: {
      handler(newVal, oldVal) {
        if (
          newVal?.query?.id &&
          newVal?.query?.n &&
          newVal.path.includes('company')
        ) {
          this.toUser(this.$route.query.id)
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapState('practicesStates', [
      'practice',
      'practiceUsers',
      'practiceUser',
      'loading',
      'pagination',
      'practiceUserPatients',
      'practiceUserPatientsPagination',
    ]),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
      'dictionaryFeatureFlagState',
    ]),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('programsStates', {
      availablePrograms: 'programs',
      availableProgramsLoading: 'loading',
    }),
    isPracticeAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
    isAnyAdminRole() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
          this.dictionaryBhUsersRoleIds.Admin ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator
      )
    },
    isConsultant() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
        this.dictionaryBhUsersRoleIds.Consultant
      )
    },
    isAdmin() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
        this.dictionaryBhUsersRoleIds.Admin
      )
    },
    doctorQueryParams() {
      return {
        roleIds: [
          this.dictionaryPracticeUserRole.Advisor,
          this.dictionaryBhUsersRoleIds.Consultant,
        ],
        isActive: true,
        sortBy: 'fullName',
        isAvailable: true,
      }
    },
    doctorsQuery() {
      const params = new URLSearchParams()
      for (const [key, value] of Object.entries(this.doctorQueryParams)) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            params.append(key, item)
          })
        } else if (value) {
          params.append(key, value)
        }
      }
      return params
    },
    practiceId() {
      return parseInt(this.$route.params.id)
    },
    computedDoctors() {
      let doctors = this.practiceDoctors.filter(
        doctor => doctor.profileId !== this.practiceUser.profileId,
      )
      return doctors.map(doctor => ({
        ...doctor,
        fullName: `${doctor.firstName} ${doctor.lastName}`,
      }))
    },
    isPracticeAdmin() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Administrator
      )
    },
    isPracticeActive() {
      return this.practice.isActive
    },
  },
  mounted() {
    if (this.$route?.query?.id) {
      this.toUser(this.$route.query.id)
    }
    if (this.isPracticeAdmin) {
      this.getPrograms({
        companyId: this.practiceId,
        active: true,
      })
    }
  },
  methods: {
    ...mapActions('practicesStates', [
      'getRawPractice',
      'getPracticeUsers',
      'addPracticeUser',
      'getPracticeUser',
      'changePracticeUser',
    ]),
    ...mapActions('bhUsersStates', ['getBhUsers']),
    ...mapActions('userStates', [
      'sendNewUserEmail',
      'toggleActivationAccount',
      'deleteUser',
    ]),
    ...mapActions('patientsStates', ['getPatient', 'changePatientField']),
    ...mapActions('programsStates', ['getPrograms']),
    toUser(id) {
      debounce(
        () => {
          this.getPracticeUser({
            practiceId: this.practiceId,
            userId: id,
          }).then(() => {
            this.showPracticeUserDialog = true
            if (!this.$route.query?.id && this.$route.query?.id != id) {
              this.$router.replace({
                path: this.$route.path,
                query: { id: id },
              })
            }
          })
        },
        100,
        'toUser',
      )
    },
    getData(data) {
      debounce(
        () => {
          this.getPracticeUsers({ id: this.practiceId, params: data })
          this.getFeatureFlagsFromPractice()
        },
        100,
        'getPracticeUsers',
      )
    },
    updateQueryParams(programs) {
      this.programs = programs
      this.queryParams.programsId = programs.map(el => el.id).join(',')
      this.getData({ ...this.queryParams, page: 0 })
    },
    async getFeatureFlagsFromPractice() {
      try {
        const practiceId = this.practiceId
        if (!practiceId) return
        const practice = await this.getRawPractice(practiceId)
        if (practice) {
          const featureFlagState = Object.keys(this.dictionaryFeatureFlagState)
          if (practice.surveyAvailable)
            practice.surveyAvailable =
              featureFlagState.find(
                flagState =>
                  flagState.toLowerCase() ===
                  practice.surveyAvailable.toLowerCase(),
              ) || practice.surveyAvailable
        }
      } catch (err) {
        console.error('Cannot get feature flags settings from practice', {
          err,
        })
        this.surveyAvailable = 'Inactive'
      }
    },
    addNewPracticeUser() {
      this.showNewPracticeUserDialog = true
    },
    closeNewPracticeUserDialog() {
      this.showNewPracticeUserDialog = false
    },
    onNewUserSubmit(userData) {
      this.addPracticeUser({ id: this.practiceId, data: userData })
    },
    closePracticeUserDialog() {
      this.showPracticeUserDialog = false
      this.$router.replace({ path: this.$route.path, query: {} })
    },
    closePracticeUserDeactivationDialog() {
      this.showDeactivationDialog = false
    },
    onUserSubmit(userData) {
      this.showPracticeUserDialog = false
      this.changePracticeUser({
        practiceId: this.practiceId,
        userId: userData.id,
        data: userData,
      })
    },
    prepareSendNewUserEmail() {
      this.sendNewUserEmail({ profileId: this.practiceUser.profileId })
    },
    async changeAdvisor(participantId, advisor) {
      await this.getPatient(participantId)
      this.changePatientField({
        key: 'practiceUserId',
        value: advisor.value,
        id: participantId,
      }).then(() => {
        this.deactivationDialogReloader++
      })
    },
    toggleActiveState() {
      if (!this.practiceId || !this.practiceUser.id) return
      this.toggleActivationAccount({
        url: `/practices/${this.practiceId}/users/${this.practiceUser.id}/active`,
        isActive: !this.practiceUser.isActive,
        version: this.practiceUser.version,
      }).then(() => {
        this.getPracticeUser({
          practiceId: this.practiceId,
          userId: this.practiceUser.id,
        })
      })
    },
    onCloseConfirmationDeleteDialog() {
      this.showConfirmationPopupDelete = false
    },
    deletePracticeUser() {
      if (!this.practiceUser.isActive) {
        this.deleteUser({
          url: `/practices/${this.practiceId}/users/${this.practiceUser.id}`,
        })
          .then(() => {
            this.$router.replace({ path: this.$route.path, query: {} })
          })
          .finally(() => {
            this.showConfirmationPopupDelete = false
            this.getData({ ...this.queryParams, page: 0 })
          })
      } else {
        this.showConfirmationPopupDelete = false
      }
      this.showPracticeUserDialog = false
    },
    onClickOutsideDialog() {
      this.confirmationPopupWelcomeMessage = false
    },
    onKeydownDialog(key) {
      if (key.code === 'Escape' && key.isTrusted) this.onClickOutsideDialog()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.staff {
  padding: 32px;
  &__tabs {
    flex: initial;
    width: auto;
    padding: 4px;
    border: 1px solid $bh-blue--4;

    &--wrap {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .v-tab {
      color: $bh-blue;
      font-weight: 700;

      &--active {
        background: $bh-blue;
        color: $bh-white !important;
      }
    }
  }

  &--wrap {
    display: flex;
  }

  &-sidebar {
    width: 272px;
    padding: 24px;

    textarea:not([readonly]) {
      padding: 4px;
    }
  }

  &-action-btn {
    cursor: pointer;
    font-weight: 600;
  }

  &-field {
    margin-bottom: $gutter;
  }

  label.bh-text-input__label {
    font-weight: 700;
    line-height: 1.425rem;
    color: $bh-gray--2;
    margin-bottom: 3px;
  }

  .programs_filter {
    max-width: 250px;
    width: 100%;
    margin-bottom: 24px;
  }
}
</style>
